import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisHorizontalIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
} from "@heroicons/react/20/solid";
import MainSearchBar from "../../../components/MainSearchbar";
import CustomButton from "../../../components/CustomButton";
import FIltersDropdown from "../../../components/Filters/FiltersDropdown";
import Pagination from "../../../components/CustomPagination";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import UserDetailsDrawer from "../../../components/Drawers/UserDetailsDrawer";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import { useNavigate } from "react-router-dom";
import {
  useGetProviderMasterUsers,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../../../custom-hooks/queries";
import { Menu, Transition } from "@headlessui/react";
import { BasicProfile } from "../../../components/BasicProfile";
import { CompanyProfile } from "../../../components/CompanyProfile";
import { AddProviderUserForm } from "../../../components/AddProviderUserForm";
import { StickyActionsTd, StickyActionsTh } from "../../../components/StickyActionsCells";

const TableSkeleton = () => (
  <div className="animate-pulse">
    <div className="min-w-full divide-y divide-gray-300">
      <div className="bg-gray-100 h-12"></div>
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex items-center space-x-4 py-4 px-3 border-b"
        >
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded-full w-8"></div>
        </div>
      ))}
    </div>
  </div>
);

const getStatusColor = (isActive) => {
  return isActive
    ? { bg: "bg-green-50", text: "text-green-700" }
    : { bg: "bg-red-50", text: "text-red-700" };
};

const Providers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("accounts");
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const { data: providers } = useGetprovidersListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  const [selectedIds, setSelectedIds] = useState({
    provider: [],
    services: [],
  });
  const filters = [
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },
    {
      id: "services",
      name: "Services",
      options: rfis,
      total: 33,
    },
  ];
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchQuery = useSelector(
    (state) => state.providersFilters.searchTextByPage["myDocuments"]
  );

  const debouncedSearch = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({ pageKey: "myDocuments", searchText: search })
      );
    }, 300),
    [dispatch]
  );

  const { data: userData, isLoading } = useGetProviderMasterUsers(currentPage);

  const handleExpandRow = (id, details) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      dispatch(setDrawer(false));
    } else {
      setExpandedRow(id);
      dispatch(setDrawer(true));
    }
  };

  const userListData = userData || {
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    items: [],
  };

  const handleAddNewAccount = () => {
    navigate("/Backend/Users/AddProviderUser");
  };

  if (isLoading) {
    return (
      <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
        <div className="border-b border-gray-200 px-5 py-5 flex items-center justify-between">
          <h1 className="font-semibold flex items-center">
            <span>Users</span>
            <div className="border-l border-gray-300 h-6 mx-3"></div>
            <span>Vendors</span>
          </h1>
        </div>
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="border-b border-gray-200 px-5 py-5 flex items-center justify-between">
        <h1 className="font-semibold flex items-center">
          <span>Users</span>
          <div className="border-l border-gray-300 h-6 mx-3"></div>
          <span>Vendors</span>
        </h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mt-2">
        <nav className="-mb-px flex space-x-8 px-4">
          <button
            onClick={() => setActiveTab("basic")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium ${
              activeTab === "basic"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Basic Profile
          </button>
          <button
            onClick={() => setActiveTab("company")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium ${
              activeTab === "company"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Company Profile
          </button>
          <button
            onClick={() => setActiveTab("accounts")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium ${
              activeTab === "accounts"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Accounts
          </button>
        </nav>
      </div>

      {/* Basic Profile Tab */}
      {activeTab === "basic" && <BasicProfile />}

      {/* Company Profile Tab */}
      {activeTab === "company" && <CompanyProfile />}

      {/* Accounts Tab */}
      {activeTab === "accounts" && (
        <div className="mt-8">
          <div className="flex justify-end items-center mb-5 px-0">
            <div className="flex justify-end">
              <div style={{ marginTop: "10px", marginLeft: "10px" }}>
                <CustomButton
                  color="bg-indigo-600"
                  size="text-base"
                  weight="font-normal"
                  height="h-9"
                  width="w-32"
                  textColor="text-white"
                  text="Add User"
                  borderRadius="0.375rem"
                  onClick={handleAddNewAccount}
                />
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block w-full py-2 align-middle">
                <div className="overflow-x-auto lg:overflow-x-visible">
                  <table className="min-w-full divide-y divide-gray-300 table-fixed">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="w-1/3 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                          Vendor Name
                        </th>
                        <th className="w-1/3 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                          Email Address
                        </th>
                        <th className="w-1/3 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                          Status
                        </th>
                          <StickyActionsTh tone="gray-100" />
                      </tr>
                    </thead>
                    <tbody>
                      {userListData.items.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr className="border-b border-gray-200">
                            <td className="px-3 py-4 text-sm text-gray-500">
                              {item.full_name}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                              {item.email}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                              {(() => {
                                const colors = getStatusColor(item.is_active);
                                return (
                                  <span
                                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${colors.bg} ${colors.text} ring-1 ring-inset ring-green-600/20`}
                                  >
                                    {item.is_active ? "Active" : "Inactive"}
                                  </span>
                                );
                              })()}
                            </td>
                            <StickyActionsTd>
                              <div className="relative">
                                <Menu
                                  as="div"
                                  className="relative inline-block text-left"
                                >
                                  <Menu.Button className="p-1 rounded-full hover:bg-gray-100">
                                    <EllipsisHorizontalIcon className="h-5 w-5 text-gray-400" />
                                  </Menu.Button>
                                  <Transition
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                  >
                                    <Menu.Items
                                      anchor="bottom end"
                                      portal
                                      className="z-[100] mt-2 w-36 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    >
                                      <div className="py-1">
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() =>
                                                handleExpandRow(item.id)
                                              }
                                              className={`${
                                                active ? "bg-gray-100" : ""
                                              } block w-full px-4 py-2 text-sm text-gray-700 text-left`}
                                            >
                                              View Details
                                            </button>
                                          )}
                                        </Menu.Item>
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() =>
                                                navigate(
                                                  `/Backend/Users/AddVendorAccount/${item.id}`
                                                )
                                              }
                                              className={`${
                                                active ? "bg-gray-100" : ""
                                              } block w-full px-4 py-2 text-sm text-gray-700 text-left`}
                                            >
                                              Manage User
                                            </button>
                                          )}
                                        </Menu.Item>
                                      </div>
                                    </Menu.Items>
                                  </Transition>
                                </Menu>
                              </div>
                            </StickyActionsTd>
                          </tr>

                          {expandedRow === item.id && (
                            <UserDetailsDrawer details={item} />
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalPages={userListData.total_pages}
          />
        </div>
      )}

      {showAddUserForm && (
        <AddProviderUserForm onClose={() => setShowAddUserForm(false)} />
      )}
    </div>
  );
};

export default Providers;
